import { useState } from "react";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { makeStyles } from "@mui/styles";
import { PieChart, pieArcLabelClasses } from "@mui/x-charts/PieChart";
import { calibrationService } from "../../../services/CalibrationService";
import { formatCalibrationDttmUTC } from "./util";

const useStyles = makeStyles({
    panel: {
        display: "flex",
        justifyContent: "space-between",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "24px",
        backgroundColor: "#f7f7f7",
        marginBottom: "24px",
        height: "500px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    },
    collapsedPanel: {
        display: "flex",
        justifyContent: "space-between",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "24px",
        backgroundColor: "#f7f7f7",
        marginBottom: "24px",
        height: "60px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    },
    headerContainer: {
        display: "flex",
        alignItems: "center",
        marginBottom: "10px",
    },
    formControl: {
        marginLeft: "15px",
        minWidth: 120,
        maxWidth: 200,
    },
    pieContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "50%",
        height: "100%",
    },
    jobsContainer: {
        width: "50%",
        height: "100%",
        overflowY: "scroll",
        paddingRight: "16px",
        borderRight: "1px solid #e0e0e0",
    },
    processDetail: {
        padding: "8px 0",
        borderBottom: "1px solid #e0e0e0",
        marginBottom: "8px",
    },
    header: {
        color: "#333",
    },
    subheader: {
        color: "#777",
    },
});

function ProcessEntry({ process, successColor, failureColor, formatDate }) {
    const [isLoading, setIsLoading] = useState(false);

    const direction = (window.ENV || "dev") === "dev" ? "promote" : "demote";

    const handlePromoteDemote = async () => {
        try {
            setIsLoading(true);
            await calibrationService.promoteOrDemoteAllDatasetsByProcessRunId(process.process_run_id, direction);
        } catch (error) {
            const actionWord = direction === "promote" ? "promoting" : "demoting";
            console.error(`Error ${actionWord} datasets:`, error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div key={process.id}>
            <Typography variant="body1">
                {process.status === "success" ? (
                    <CheckCircleIcon style={{ color: successColor, verticalAlign: "middle", marginRight: "4px" }} />
                ) : (
                    <CancelIcon style={{ color: failureColor, verticalAlign: "middle", marginRight: "4px" }} />
                )}
                <span style={{ fontSize: "20px " }}>{process.process_run_id}</span>
            </Typography>
            {process.client_name && (
                <Typography variant="body2" color="textSecondary">
                    Client: {process.client_name} | Project: {process.project_name}
                </Typography>
            )}
            <Typography variant="body2" color="textSecondary">
                Type: {process.process_type}
            </Typography>
            <Typography variant="body2" color="textSecondary">
                Completed at {formatDate(process.completed_at)} (
                {((new Date(process.completed_at) - new Date(process.started_at)) / 60000).toFixed(2)} minutes) <br />
                Status:{" "}
                <span style={{ color: process.status === "success" ? successColor : failureColor }}>
                    {process.status}
                </span>
            </Typography>
            {process.process_group && (
                <Typography variant="body2" color="textSecondary">
                    Group: {process.process_group}
                </Typography>
            )}
            {process.message && (
                <Typography variant="body2" color="textSecondary">
                    Message: {process.message}
                </Typography>
            )}
            <Typography variant="caption" color="textSecondary">
                Owners: {process.owner_emails.map((email) => email).join(", ")}
            </Typography>
            <Box mt={1}>
                <Button variant="contained" size="small" onClick={handlePromoteDemote} disabled={isLoading}>
                    {direction.charAt(0).toUpperCase() + direction.slice(1)} Inputs
                </Button>
            </Box>
        </div>
    );
}

export default function CompletedProcessesPanel({ processes, setTimeBeforeStartInDays }) {
    const classes = useStyles();
    const [timeRange, setTimeRange] = useState("1");
    const [statusFilter, setStatusFilter] = useState("all");

    // For short time ranges (15 min, 1 hour), filter client-side from 1 day of data
    // For longer ranges, use the API's timeBeforeStartInDays parameter
    const timeFilteredProcesses = (() => {
        const now = new Date();
        if (timeRange === "15min") {
            const cutoff = new Date(now.getTime() - 15 * 60 * 1000);
            return processes.filter((p) => new Date(p.completed_at) >= cutoff);
        } else if (timeRange === "1hour") {
            const cutoff = new Date(now.getTime() - 60 * 60 * 1000);
            return processes.filter((p) => new Date(p.completed_at) >= cutoff);
        }
        return processes;
    })();

    // Filter processes based on status selection
    const filteredProcesses =
        statusFilter === "all"
            ? timeFilteredProcesses
            : statusFilter === "success"
              ? timeFilteredProcesses.filter((p) => p.status === "success")
              : timeFilteredProcesses.filter((p) => p.status !== "success");

    const successfulProcesses = filteredProcesses.filter((p) => p.status === "success").length;
    const failedProcesses = filteredProcesses.length - successfulProcesses;

    const successColor = "#4CAF50"; // Green
    const failureColor = "#FF5252"; // Red

    // In case of 100% success or 100% failure, we would like to only have one label and one color in the pie chart.
    // To do this, if the length is 0, we just keep an empty array
    const pieChartData = [
        ...(successfulProcesses > 0
            ? [{ id: 0, value: successfulProcesses, label: "Success", color: successColor }]
            : []),
        ...(failedProcesses > 0 ? [{ id: 1, value: failedProcesses, label: "Failed", color: failureColor }] : []),
    ];

    // formatDate is the shared cal-plat UTC formatter; produces e.g. "2026-05-26 15:17 UTC".
    const formatDate = formatCalibrationDttmUTC;

    const handleTimeChange = (event) => {
        const value = event.target.value;
        setTimeRange(value);
        // For short time ranges, fetch 1 day of data and filter client-side
        // For longer ranges, use the API parameter directly
        if (value === "15min" || value === "1hour") {
            setTimeBeforeStartInDays("1");
        } else {
            setTimeBeforeStartInDays(value);
        }
    };

    const handleStatusChange = (event) => {
        setStatusFilter(event.target.value);
    };

    return (
        <>
            <div className={classes.headerContainer}>
                <Typography variant="h6" gutterBottom>
                    Completed Processes:
                </Typography>
                <FormControl variant="standard" className={classes.formControl}>
                    <InputLabel className={classes.inputLabel}>Time Range</InputLabel>
                    <Select value={timeRange} onChange={handleTimeChange} label="Time Range">
                        <MenuItem value="15min">15 minutes</MenuItem>
                        <MenuItem value="1hour">1 hour</MenuItem>
                        <MenuItem value="1">1 day</MenuItem>
                        <MenuItem value="7">1 week</MenuItem>
                        <MenuItem value="30">1 month</MenuItem>
                        <MenuItem value="365">1 year</MenuItem>
                        <MenuItem value="3650">10 years</MenuItem>
                    </Select>
                </FormControl>
                <FormControl variant="standard" className={classes.formControl}>
                    <InputLabel className={classes.inputLabel}>Status</InputLabel>
                    <Select value={statusFilter} onChange={handleStatusChange} label="Status">
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="success">Successful</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                </FormControl>
            </div>
            <div className={filteredProcesses.length !== 0 ? classes.panel : classes.collapsedPanel}>
                <div className={classes.jobsContainer}>
                    {filteredProcesses.map((process) => (
                        <div key={process.id} className={classes.processDetail}>
                            <ProcessEntry
                                process={process}
                                successColor={successColor}
                                failureColor={failureColor}
                                formatDate={formatDate}
                            />
                        </div>
                    ))}
                </div>
                <div className={classes.pieContainer}>
                    {successfulProcesses || failedProcesses ? (
                        <>
                            <Typography variant="h6">Completed Processes Summary</Typography>
                            <PieChart
                                colors={pieChartData.map((item) => item.color)}
                                series={[
                                    {
                                        data: pieChartData,
                                        arcLabel: (item) => `${item.label} (${item.value})`,
                                        innerRadius: 30,
                                        outerRadius: 100,
                                        paddingAngle: successfulProcesses === 0 || failedProcesses === 0 ? 0 : 5,
                                        cornerRadius: 0,
                                        cx: 200,
                                        cy: 200,
                                    },
                                ]}
                                legend={{ hidden: true }}
                                width={400}
                                height={400}
                                sx={{
                                    [`& .${pieArcLabelClasses.root}`]: {
                                        fill: "#555",
                                        fontWeight: "500",
                                        fontSize: "11px",
                                    },
                                }}
                            />
                        </>
                    ) : (
                        <Typography variant="h6" gutterBottom>
                            No data to display
                        </Typography>
                    )}
                </div>
            </div>
        </>
    );
}
